// Layout for the /scada/* dispatcher section. Reuses DashboardLayout (sidebar,
// topbar, auth gate) and renders a dark content surface + horizontal sub-nav.
// Forces dark visuals locally without mutating the user's saved theme preference.
import type { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardLayout from './DashboardLayout';
import { scada } from '@/scada/tokens';

interface SubNavItem { key: string; label: string; path: string; icon: string; stage: string; }

const SUBNAV: SubNavItem[] = [
  { key: 'dispatch', label: 'Диспетчерська', path: '/scada/dispatch', icon: 'ri-grid-line', stage: 'R1' },
  { key: 'control', label: 'Керування', path: '/scada/control', icon: 'ri-sliders-line', stage: 'R1' },
  { key: 'sko', label: 'СКО / ОСР', path: '/scada/sko', icon: 'ri-shield-flash-line', stage: 'R3' },
  { key: 'events', label: 'Події', path: '/scada/events', icon: 'ri-alarm-warning-line', stage: 'R3' },
  { key: 'shift', label: 'Журнал зміни', path: '/scada/shift-handover', icon: 'ri-booklet-line', stage: 'R3' },
  { key: 'emsbox', label: 'Звʼязок', path: '/scada/emsbox', icon: 'ri-router-line', stage: 'R4' },
  { key: 'reports', label: 'Звіти', path: '/scada/reports', icon: 'ri-file-chart-line', stage: 'R4' },
];

export default function ScadaLayout({ active, children }: { active: string; children: ReactNode }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();

  return (
    <DashboardLayout breadcrumb={[t('d_bc_dispatch'), 'SCADA']}>
      {/* Full-bleed dark surface covering the (possibly light) cabinet content area */}
      <div style={{ background: scada.bg, margin: -16, padding: 16, minHeight: 'calc(100vh - 72px)' }} className="md:-m-6 md:p-6">
        {/* Header + sub-nav */}
        <div className="flex items-center justify-between flex-wrap gap-2" style={{ marginBottom: 16 }}>
          <div className="flex items-center gap-2">
            <i className="ri-cpu-line" style={{ color: scada.accent, fontSize: 20 }} />
            <span style={{ color: scada.text, fontSize: 18, fontWeight: 700 }}>SCADA — Диспетчеризація</span>
            <span title="Телеметрія в реальному часі з діючих СЕС (оновлення за циклом збору даних)"
              className="inline-flex items-center gap-1.5"
              style={{ background: `${scada.state.ok}1A`, color: scada.state.ok, border: `1px solid ${scada.state.ok}40`, borderRadius: 999, padding: '2px 10px', fontSize: 10, fontWeight: 700 }}>
              <span style={{ width: 6, height: 6, borderRadius: 999, background: scada.state.ok, display: 'inline-block' }} />
              LIVE
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1 flex-wrap" style={{ marginBottom: 16, borderBottom: `1px solid ${scada.border}`, paddingBottom: 8 }}>
          {SUBNAV.map((it) => {
            const on = active === it.key || location.pathname === it.path;
            return (
              <button key={it.key} onClick={() => navigate(it.path)}
                className="inline-flex items-center gap-1.5 whitespace-nowrap"
                style={{
                  background: on ? `${scada.accent}1A` : 'transparent',
                  color: on ? scada.text : scada.textDim,
                  border: `1px solid ${on ? scada.accent + '60' : 'transparent'}`,
                  borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer',
                }}>
                <i className={it.icon} style={{ color: on ? scada.accent : scada.textMuted }} />
                {it.label}
              </button>
            );
          })}
        </div>

        {children}
      </div>
    </DashboardLayout>
  );
}
